import React from 'react';
import { FolderOpenIcon, WrenchScrewdriverIcon, BookOpenIcon, AcademicCapIcon, RocketLaunchIcon, DocumentTextIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ResourceList from '../../../components/playbooks/ResourceList';

const templatesAndTools = [
  {
    title: "Open Org's Performance Playbook Template",
    description: "Complete Notion template with all sections, forms, and guidance for building your performance framework",
    url: "https://www.notion.so/Open-Org-s-Performance-Playbook-Template-1b3f015e5d468046bdceffe4cf92200c?pvs=21"
  },
  {
    title: "Competency & Career Framework Designer (GPT Tool)",
    description: "AI-powered tool to help accelerate building a basic competency framework V1",
    url: "https://www.openverse.fyi/resource/competency-and-career-framework-designer-gpt-tool"
  },
  {
    title: "Open Org's Culture Heat-map",
    description: "Quick 5-minute personal diagnostic to help you surface areas across your culture & ways of work that need attention",
    url: "https://www.notion.so/open-org/11ff015e5d4680fb9d4afce35ec36ac8?v=11ff015e5d468125bfbb000ccece2596&source=copy_link"
  },
  {
    title: "Culture Design Toolkit (Miro)",
    description: "Comprehensive toolkit for assessing and designing organizational culture and ways of working",
    url: "https://miro.com/miroverse/open-org-culture-design-toolkit/?social=copy-link"
  },
  {
    title: "7-Step Values Design and Evolution Blueprint",
    description: "Complete blueprint for designing and embedding organizational values and behaviors",
    url: "https://www.openverse.fyi/resource/7-step-values-design-and-evolution-blueprint"
  },
  {
    title: "Performance Framework Launch Checklist",
    description: "Step-by-step checklist for successfully launching your performance framework",
    url: "https://www.notion.so/performance-launch-checklist"
  }
];

const exampleFrameworks = [
  {
    title: "Juro's Performance Framework",
    description: "Excellent example of a lightweight, transparent performance framework with clear rating scales",
    url: "https://juro.notion.site/Performance-framework-1afc0a7408c3498bbd888352653f4cff"
  },
  {
    title: "Dune Analytics Careers Framework",
    description: "Comprehensive approach to careers and performance with clear progression paths",
    url: "https://duneanalytics.notion.site/Careers-at-Dune-f25e0b5e1f624d2aaf3ef2c81558ea96"
  },
  {
    title: "Sourcegraph's High Performance Culture",
    description: "Deep dive into teammate development and performance management practices",
    url: "https://sourcegraph.notion.site/High-Performance-Culture-Teammate-Development-2d30e81c0c1a42b9b39beaab8eaed4bb"
  },
  {
    title: "Toggl's Performance Measurement",
    description: "Transparent approach to measuring and evaluating team performance",
    url: "https://toggl.notion.site/How-We-Measure-Performance-7d0fd16c0ab749f5b37cddfc6e30511d"
  },
  {
    title: "Quantum Light Capital Performance Playbook",
    description: "Practical performance management approach for growing teams",
    url: "https://quantumlightcapital.com/playbooks/performance-management"
  },
  {
    title: "Learnerbly's Performance Employee Hub",
    description: "Employee-focused performance hub with clear guidance and resources",
    url: "https://www.notion.so/Performance-Employee-Hub-OS-f6ba7d4133a54a64a180e20e7a253b34"
  }
];

const booksAndArticles = [
  {
    title: "Radical Candor by Kim Scott",
    description: "Essential read on giving feedback and building caring, challenging relationships",
    url: "https://www.amazon.com/Radical-Candor-Kick-Ass-Without-Humanity/dp/1250103509"
  },
  {
    title: "The Culture Code by Daniel Coyle",
    description: "Understanding how high-performing teams build culture and accountability",
    url: "https://www.amazon.com/Culture-Code-Secrets-Highly-Successful/dp/0804176981"
  },
  {
    title: "First, Break All the Rules by Marcus Buckingham",
    description: "Research-based insights on what great managers do differently",
    url: "https://www.amazon.com/First-Break-All-Rules-Differently/dp/1595621113"
  },
  {
    title: "The Hard Thing About Hard Things by Ben Horowitz",
    description: "Practical advice on making difficult decisions, including performance management",
    url: "https://www.amazon.com/Hard-Thing-About-Things-Building/dp/0062273205"
  }
];

const performanceTools = [
  {
    title: "Crewmojo",
    description: "Modern performance management platform focused on continuous feedback and development",
    url: "https://crewmojo.com/"
  },
  {
    title: "Incompass",
    description: "Employee engagement and performance platform with powerful analytics",
    url: "https://incompass.com/"
  },
  {
    title: "Pandria",
    description: "Comprehensive performance management solution for growing teams",
    url: "https://pandria.com/"
  },
  {
    title: "Pensero",
    description: "AI-powered performance insights and continuous feedback platform",
    url: "https://pensero.com/"
  }
];

const PerformanceFrameworkUsefulResources = () => (
  <div className="space-y-8">
    <PlaybookSection title="Useful Resources" icon={<FolderOpenIcon className="h-7 w-7 text-primary-light" />} heading>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <PlaybookCard>
          <ResourceList 
            title="Templates & Tools" 
            items={templatesAndTools} 
            icon={WrenchScrewdriverIcon}
          />
        </PlaybookCard>
        <PlaybookCard>
          <ResourceList 
            title="Example Frameworks" 
            items={exampleFrameworks} 
            icon={DocumentTextIcon}
          />
        </PlaybookCard>
        <PlaybookCard>
          <ResourceList 
            title="Books & Articles" 
            items={booksAndArticles} 
            icon={BookOpenIcon}
          />
        </PlaybookCard>
        <PlaybookCard>
          <ResourceList 
            title="Performance Tools" 
            items={performanceTools} 
            icon={RocketLaunchIcon}
          />
        </PlaybookCard>
      </div>
    </PlaybookSection>
  </div>
);

export default PerformanceFrameworkUsefulResources; 